'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { NumericFormat } from 'react-number-format';
import { InputContainer } from '../InputContainer';

export const WorkersCompensationCalculator: React.FC = () => {
  const [classCodeRate, setClassCodeRate] = React.useState(1.88);
  const [experienceModifier, setExperienceModifier] = React.useState(1);
  const [averageAnnualPayroll, setAverageAnnualPayroll] = React.useState(50000);
  const [stateFee, setStateFee] = React.useState(250);
  const [numberOfEmployees, setNumberOfEmployees] = React.useState(25);
  const [annualPolicyPremium, setAnnualPolicyPremium] = React.useState(29750);

  const allValues = React.useMemo(() => {
    return { averageAnnualPayroll, classCodeRate, experienceModifier, numberOfEmployees, stateFee };
  }, [averageAnnualPayroll, classCodeRate, experienceModifier, numberOfEmployees, stateFee]);

  const calculate = React.useCallback(
    (newValues: {
      classCodeRate: number;
      experienceModifier: number;
      averageAnnualPayroll: number;
      stateFee: number;
      numberOfEmployees: number;
    }): void => {
      const classCodeRate = newValues.classCodeRate / 100;
      const newAnnualPolicyPremium =
        (newValues.averageAnnualPayroll * classCodeRate + newValues.stateFee) *
        newValues.numberOfEmployees *
        newValues.experienceModifier;
      setAnnualPolicyPremium(newAnnualPolicyPremium);
    },
    [],
  );

  const handleOnChangeClassCodeRate = React.useCallback(
    (value: number) => {
      calculate({ ...allValues, classCodeRate: value });
      setClassCodeRate(value);
    },
    [allValues, calculate],
  );

  const handleOnChangeExperienceModifier = React.useCallback(
    (value: number) => {
      calculate({ ...allValues, experienceModifier: value });
      setExperienceModifier(value);
    },
    [allValues, calculate],
  );

  const handleOnChangeAverageAnnualPayroll = React.useCallback(
    (value: number) => {
      calculate({ ...allValues, averageAnnualPayroll: value / 100 });
      setAverageAnnualPayroll(value);
    },
    [allValues, calculate],
  );

  const handleOnChangeStateFee = React.useCallback(
    (value: number) => {
      calculate({ ...allValues, stateFee: value });
      setStateFee(value);
    },
    [allValues, calculate],
  );

  const handleOnChangeNumberOfEmployees = React.useCallback(
    (value: number) => {
      calculate({ ...allValues, numberOfEmployees: value });
      setNumberOfEmployees(value);
    },
    [allValues, calculate],
  );

  const annualPolicyPremiumPerEmployee = annualPolicyPremium / numberOfEmployees;

  return (
    <Container className="workers-compensation-calculator">
      <div className="inputs-container">
        <InputContainer
          name="What is your class code rate?"
          onChange={handleOnChangeClassCodeRate}
          prefix="$"
          value={classCodeRate}
        />
        <InputContainer
          name="What is your experience modifier?"
          onChange={handleOnChangeExperienceModifier}
          value={experienceModifier}
        />
        <InputContainer
          name="What is the average annual payroll per employee?"
          onChange={handleOnChangeAverageAnnualPayroll}
          prefix="$"
          value={averageAnnualPayroll}
        />
        <InputContainer name="What is the State Fee?" onChange={handleOnChangeStateFee} prefix="$" value={stateFee} />
        <InputContainer
          name="Number of Employees"
          onChange={handleOnChangeNumberOfEmployees}
          value={numberOfEmployees}
        />
      </div>
      <div className="result-container">
        <span className="label">Annual Policy Premium:</span>
        <NumericFormat
          decimalScale={2}
          displayType="text"
          fixedDecimalScale={true}
          prefix="$"
          thousandSeparator={true}
          value={annualPolicyPremium ? annualPolicyPremium : '-'}
        />
        <div>
          <NumericFormat
            className="app-per-employee-value"
            decimalScale={2}
            displayType="text"
            fixedDecimalScale={true}
            prefix="$"
            thousandSeparator={true}
            value={annualPolicyPremiumPerEmployee ? annualPolicyPremiumPerEmployee : '-'}
          />
          <span>per employee</span>
        </div>
      </div>
    </Container>
  );
};

const Container = styled.div`
  &.workers-compensation-calculator {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .inputs-container {
    width: 100%;

    .input-container {
      margin-bottom: 10px;
      label {
        text-transform: unset;
      }
      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }

  .result-container {
    color: ${({ theme }) => theme.colorPalette.black};
    background-color: ${({ theme }) => theme.colorPalette.orange200};
    font-size: ${({ theme }) => theme.fontSize.lg};
    margin-top: 1rem;
    text-align: center;
    width: 100%;
    padding: 8px 15px;

    > div {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
    }

    .label {
      margin-right: 10px;
      ~ span {
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
      }
    }

    .app-per-employee-value {
      margin-right: 5px;
    }
  }
`;
